import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { api, PRODUCT_SORT_KEYS, type Product, type ProductQuery, type ProductSortKey, type ProductStatus } from "@/api";
import { productKeys, useProductFilterOptions, useProducts, useProductStatusCounts, useRejectionReasons } from "@/api/hooks/use-products";
import { useCategories } from "@/api/hooks/use-categories";
import { useStorefronts } from "@/api/hooks/use-storefronts";
import { productCategoryOptions } from "@/lib/category-tree";
import { AdminDetailModal } from "@/components/admin-detail-modal";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { serverDataTable, useUrlTable } from "@/lib/use-url-table";
import { ProductDetailContent } from "./product-detail";
import { SellerDetailContent } from "./seller-detail";
import { ShopDetailContent } from "./shop-detail";
import { formatPrice } from "@/lib/utils";
import { PriceModerationRule, PriceRequestsPanel } from "./price-requests-panel";
import { ComplianceRowNote, ComplianceStrip, complianceSummaryKey } from "@/components/compliance-strip";
const STATUSES: (ProductStatus | "all")[] = ["all", "moderation", "draft", "rejected", "published", "blocked", "archived"];
const STATUS_VARIANT: Record<ProductStatus, "default" | "secondary" | "outline" | "destructive"> = {
  published: "default",
  moderation: "secondary",
  draft: "outline",
  rejected: "destructive",
  blocked: "destructive",
  archived: "outline"
};
type ProductActionKind = "approve" | "reject" | "block";
/** Карточка в решении модератора: id + название для диалога и итога. */
type Picked = {
  id: string;
  name: string;
};

/** Потолок массового решения за один запрос (как на шлюзе). */
const BULK_MAX = 200;
const FILTER_KEYS = ["status", "search", "shopId", "categoryId", "teamLeadId", "awaiting", "compliance", "dateField", "from", "to"] as const;

/**
 * Товары консоли. Фильтр, поиск, сортировка и страница считаются на сервере
 * (GET /admin/products), срез живёт в адресной строке. Раньше страница
 * забирала до 500 карточек и фильтровала их в браузере: после 500 хвост
 * очереди модерации становился невидимым.
 */
export function ProductsPage() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const table = useUrlTable<ProductSortKey>({
    filters: FILTER_KEYS,
    defaultSort: {
      key: "updated",
      dir: "desc"
    },
    defaultPageSize: 25,
    sortKeys: PRODUCT_SORT_KEYS
  });
  const f = table.filters;
  const status = (STATUSES as string[]).includes(f.status ?? "") ? f.status as ProductStatus | "all" : "all";
  const query: ProductQuery = {
    search: f.search || undefined,
    shopId: f.shopId || undefined,
    categoryId: f.categoryId || undefined,
    teamLeadId: f.teamLeadId || undefined,
    awaiting: f.awaiting === "1",
    compliance: f.compliance === "missing" ? "missing" : undefined,
    dateField: f.dateField === "created" ? "created" : "updated",
    from: f.from || undefined,
    to: f.to || undefined
  };
  const productsQuery = useProducts({
    ...query,
    status,
    page: table.page,
    pageSize: table.pageSize,
    sort: table.sort.key,
    dir: table.sort.dir
  });
  // Цифры на вкладках — с сервера и в том же срезе, что таблица.
  const statusCounts = useProductStatusCounts(query).data;
  const options = useProductFilterOptions().data;
  // Фильтр категорий — по дереву: узел включает всю ветку («Женщинам» —
  // это и «Платья», и «Кеды»), число — товаров во всей ветке. Архивные узлы
  // в конце, пустые ветки не показываем.
  const categoriesQuery = useCategories();
  const categoryOptions = productCategoryOptions(categoriesQuery.data ?? [], options?.categories ?? [], {
    none: t("products.noCategory"),
    archived: t("products.filter.archivedCategory")
  });
  const items = productsQuery.data?.data ?? [];
  const total = productsQuery.data?.meta.total ?? 0;
  const view = serverDataTable(table, items, total);
  // Витрина магазина открывается по slug — список витрин короткий (по одной
  // на магазин), имя магазина же приходит в строке товара с сервера.
  const storefrontsQuery = useStorefronts();
  const slugByShop = new Map((storefrontsQuery.data ?? []).map(s => [s.id, s.slug]));

  // Поле поиска пишет в URL с паузой: запрос на каждую букву не нужен.
  const [searchInput, setSearchInput] = useState(f.search ?? "");
  const urlSearch = f.search ?? "";
  useEffect(() => setSearchInput(urlSearch), [urlSearch]);
  const setFilterRef = useRef(table.setFilter);
  setFilterRef.current = table.setFilter;
  useEffect(() => {
    if (searchInput.trim() === urlSearch) return;
    const id = setTimeout(() => setFilterRef.current("search", searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput, urlSearch]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  // Решение модератора: по одной карточке из меню строки или по выбранным
  // галочками (массовое действие) — один и тот же диалог.
  const [pending, setPending] = useState<{
    kind: ProductActionKind;
    products: Picked[];
  } | null>(null);
  const [selected, setSelected] = useState<Map<string, Picked>>(new Map());
  // «Выбраны все N по фильтру» — выбор охватывает и другие страницы.
  const [allMatching, setAllMatching] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    done: number;
    failed: {
      name: string;
      error: string;
    }[];
    /** Пропущены без документа о соответствии (массовая публикация). */
    skipped: number;
  } | null>(null);
  const [openActions, setOpenActions] = useState<string | null>(null);
  // Отклонение: типовая причина из справочника (обязательна) + комментарий.
  const [rejectionCode, setRejectionCode] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const {
    data: rejectionReasons
  } = useRejectionReasons();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedShop, setSelectedShop] = useState<{
    kind: "storefront";
    slug: string;
    title: string;
  } | {
    kind: "seller";
    userId: string;
    title: string;
  } | null>(null);

  // Выбор сбрасывается при смене среза и страницы — массовое действие
  // работает по тому, что модератор видит, и не задевает скрытые карточки.
  // Исключение — явное «выбрать все N по фильтру»: оно живёт до смены фильтра.
  const sliceKey = JSON.stringify([status, query, table.sort]);
  const pageKey = `${table.page}:${table.pageSize}`;
  const resetSelection = () => {
    setSelected(new Map());
    setAllMatching(false);
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: сброс по смене среза
  useEffect(resetSelection, [sliceKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: сброс по смене страницы
  useEffect(() => {
    if (!allMatching) setSelected(new Map());
  }, [pageKey]);
  const load = productsQuery.isPending ? "loading" : productsQuery.isError ? "error" : "ready";
  const error = actionError ?? (productsQuery.error ? productsQuery.error.message : null);
  const pageIds = items.map(p => p.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every(id_0 => selected.has(id_0));
  const toggleOne = (p_0: Product) => {
    setAllMatching(false);
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(p_0.id)) next.delete(p_0.id);else next.set(p_0.id, {
        id: p_0.id,
        name: p_0.name
      });
      return next;
    });
  };
  const togglePage = () => {
    setAllMatching(false);
    setSelected(prev_0 => {
      const next_0 = new Map(prev_0);
      for (const p_1 of items) {
        if (allOnPageSelected) next_0.delete(p_1.id);else next_0.set(p_1.id, {
          id: p_1.id,
          name: p_1.name
        });
      }
      return next_0;
    });
  };
  // «Выбрать все N по фильтру»: шлюз отдаёт id всего среза, если он не больше
  // потолка массового действия. Больше — предлагаем сузить фильтр.
  const selectAllMatching = async () => {
    setSelectingAll(true);
    setActionError(null);
    try {
      const res = await api.products.ids({
        ...query,
        status
      });
      if (res.ids.length === 0) return;
      const names = new Map(items.map(p_2 => [p_2.id, p_2.name]));
      setSelected(new Map(res.ids.map(id_1 => [id_1, {
        id: id_1,
        name: names.get(id_1) ?? id_1
      }])));
      setAllMatching(true);
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setSelectingAll(false);
    }
  };
  const selectedProducts = [...selected.values()];
  const confirmAction = async () => {
    if (!pending) return;
    const {
      kind,
      products
    } = pending;
    if (kind === "reject" && !rejectionCode) {
      setActionError(t("productDetail.rejectReasonRequired"));
      return;
    }
    if (kind === "block" && !rejectionReason.trim()) {
      setActionError(t("products.block.reasonRequired"));
      return;
    }
    setActionError(null);
    setActing(products.length === 1 ? products[0]?.id ?? "bulk" : "bulk");
    try {
      if (products.length === 1 && products[0]) {
        const product = products[0];
        if (kind === "approve") await api.products.approve(product.id);else if (kind === "block") await api.products.block(product.id, rejectionReason.trim());else await api.products.reject(product.id, {
          reasonCode: rejectionCode,
          reason: rejectionReason.trim() || undefined
        });
        setBulkResult(null);
      } else {
        const res_0 = await api.products.bulk({
          action: kind,
          ids: products.map(p_3 => p_3.id),
          reasonCode: kind === "reject" ? rejectionCode : undefined,
          reason: rejectionReason.trim() || undefined
        });
        const nameOf = (id_2: string) => products.find(p_4 => p_4.id === id_2)?.name ?? id_2;
        setBulkResult({
          done: res_0.done.length,
          failed: res_0.failed.map(f_0 => ({
            name: nameOf(f_0.id),
            error: f_0.error
          })),
          skipped: res_0.skippedNoDocument?.length ?? 0
        });
      }
      resetSelection();
      setPending(null);
      setRejectionCode("");
      setRejectionReason("");
      await queryClient.invalidateQueries({
        queryKey: productKeys.all
      });
      void queryClient.invalidateQueries({
        queryKey: complianceSummaryKey
      });
    } catch (e_0) {
      setActionError((e_0 as Error).message);
    } finally {
      setActing(null);
    }
  };
  const dialogKey = (suffix: "Title" | "Body" | "Label") => `products_dialog.${pending?.kind ?? "approve"}${suffix}`;
  const openShop = (p_5: Product) => {
    const title = p_5.shopName ?? p_5.shopId;
    const slug = slugByShop.get(p_5.shopId);
    if (slug) setSelectedShop({
      kind: "storefront",
      slug,
      title
    });else if (p_5.shopOwnerId) setSelectedShop({
      kind: "seller",
      userId: p_5.shopOwnerId,
      title
    });
  };
  return <div className="space-y-6">
			<div data-tour="products-status" className="flex flex-wrap items-center gap-2">
				{STATUSES.map(s_0 => <Button key={s_0} variant={s_0 === status ? "default" : "outline"} size="sm" onClick={() => table.setFilter("status", s_0 === "all" ? "" : s_0)}>
						{t(`products.status.${s_0}`)}
						{statusCounts?.[s_0] != null && <span className="ml-1.5 tabular-nums opacity-70">{statusCounts[s_0]}</span>}
					</Button>)}
			</div>

			<ComplianceStrip active={query.compliance === "missing"} status={status} onShow={next_1 => table.setFilters({
      status: next_1,
      compliance: "missing"
    })} onReset={() => table.setFilters({
      compliance: ""
    })} />

			<div data-tour="products-filters" className="flex flex-wrap items-center gap-2">
				<Input value={searchInput} onChange={e_1 => setSearchInput(e_1.target.value)} placeholder={t("products.searchPlaceholder")} aria-label={t("products.searchPlaceholder")} className="h-9 w-full sm:max-w-xs" />
				<Select value={f.shopId ?? ""} aria-label={t("products.allShops")} data-testid="products-filter-shop" onChange={e_2 => table.setFilter("shopId", e_2.target.value)} className="w-full sm:w-56">
					<option value="">{t("products.allShops")}</option>
					{(options?.shops ?? []).map(s_1 => <option key={s_1.id} value={s_1.id}>
							{s_1.name} ({s_1.count})
						</option>)}
				</Select>
				<Select value={f.categoryId ?? ""} aria-label={t("products.allCategories")} title={t("products.filter.categoryBranchHint")} data-testid="products-filter-category" onChange={e_3 => table.setFilter("categoryId", e_3.target.value)} className="w-full sm:w-64">
					<option value="">{t("products.allCategories")}</option>
					{categoryOptions.map(o => <option key={o.id} value={o.id}>
							{o.label} ({o.count})
						</option>)}
				</Select>
				{(options?.teamLeads.length ?? 0) > 0 && <Select value={f.teamLeadId ?? ""} aria-label={t("products.filter.allTeamLeads")} data-testid="products-filter-teamlead" onChange={e_4 => table.setFilter("teamLeadId", e_4.target.value)} className="w-full sm:w-48">
						<option value="">{t("products.filter.allTeamLeads")}</option>
						{(options?.teamLeads ?? []).map(l => <option key={l.id} value={l.id}>
								{l.name}
							</option>)}
					</Select>}
				<label className="flex h-9 items-center gap-2 rounded-md border px-3 text-sm">
					<input type="checkbox" className="size-4" data-testid="products-filter-awaiting" checked={f.awaiting === "1"} onChange={e_5 => table.setFilter("awaiting", e_5.target.checked ? "1" : "")} />
					{t("products.filter.awaiting")}
				</label>
				<div className="flex flex-wrap items-center gap-1.5">
					<Select value={f.dateField === "created" ? "created" : "updated"} aria-label={t("products.filter.dateField")} onChange={e_6 => table.setFilter("dateField", e_6.target.value === "created" ? "created" : "")} className="h-9 w-[8.5rem]">
						<option value="updated">{t("products.filter.updated")}</option>
						<option value="created">{t("products.filter.created")}</option>
					</Select>
					<Input type="date" aria-label={t("products.filter.from")} title={t("products.filter.from")} className="h-9 w-[9.5rem]" value={f.from ?? ""} max={f.to || undefined} onChange={e_7 => table.setFilter("from", e_7.target.value)} />
					<span className="text-xs text-muted-foreground">—</span>
					<Input type="date" aria-label={t("products.filter.to")} title={t("products.filter.to")} className="h-9 w-[9.5rem]" value={f.to ?? ""} min={f.from || undefined} onChange={e_8 => table.setFilter("to", e_8.target.value)} />
				</div>
				{table.hasFilters && <Button size="sm" variant="ghost" onClick={() => {
        setSearchInput("");
        table.resetFilters();
      }}>
						{t("products.filter.reset")}
					</Button>}
			</div>

			<PriceModerationRule />

			<PriceRequestsPanel />

			{error && !pending && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{error}
				</div>}

			{bulkResult && <div role="status" className={`flex items-start justify-between gap-3 rounded-md border p-3 text-sm ${bulkResult.failed.length || bulkResult.skipped ? "border-amber-300 bg-amber-50" : "bg-muted/40"}`} data-testid="products-bulk-result">
					<div className="space-y-1">
						<div>
							{t("products.bulk.resultDone", {
            count: bulkResult.done
          })}
							{bulkResult.failed.length > 0 && ` · ${t("products.bulk.resultFailed", {
            count: bulkResult.failed.length
          })}`}
							{bulkResult.skipped > 0 && ` · ${t("compliance.products.skipped", {
            count: bulkResult.skipped
          })}`}
						</div>
						{bulkResult.failed.map(f_1 => <div key={f_1.name} className="text-xs text-muted-foreground">
								{f_1.name}: {f_1.error}
							</div>)}
					</div>
					<Button size="sm" variant="ghost" onClick={() => setBulkResult(null)}>
						{t("products.bulk.dismiss")}
					</Button>
				</div>}

			{selected.size > 0 && <div data-testid="products-bulk-bar" className="sticky top-2 z-20 flex flex-wrap items-center gap-2 rounded-md border bg-background p-2 shadow-sm">
					<span className="px-2 text-sm font-medium">
						{allMatching ? t("products.bulk.allMatchingSelected", {
          count: selected.size
        }) : t("products.bulk.selected", {
          count: selected.size
        })}
					</span>
					<Button size="sm" disabled={!!acting} onClick={() => setPending({
        kind: "approve",
        products: selectedProducts
      })}>
						{t("products.actions.approve")}
					</Button>
					<Button size="sm" variant="outline" disabled={!!acting} onClick={() => setPending({
        kind: "reject",
        products: selectedProducts
      })}>
						{t("products.actions.reject")}
					</Button>
					<Button size="sm" variant="outline" className="text-destructive" disabled={!!acting} onClick={() => setPending({
        kind: "block",
        products: selectedProducts
      })}>
						{t("products.actions.block")}
					</Button>
					{/* Весь срез за пределами страницы — только по явной кнопке и в
         пределах потолка массового действия. */}
					{allOnPageSelected && !allMatching && total > items.length && (total <= BULK_MAX ? <Button size="sm" variant="ghost" data-testid="products-select-all-matching" disabled={selectingAll} onClick={selectAllMatching}>
								{t("products.bulk.selectAllMatching", {
          count: total
        })}
							</Button> : <span className="px-2 text-xs text-muted-foreground">
								{t("products.bulk.tooManyMatching", {
          count: total,
          max: BULK_MAX
        })}
							</span>)}
					<Button size="sm" variant="ghost" onClick={resetSelection}>
						{t("products.bulk.clear")}
					</Button>
				</div>}

			<Table data-tour="products-table">
				<TableHeader>
					<TableRow>
						<TableHead className="w-10">
							<input type="checkbox" className="size-4 align-middle" aria-label={t("products.bulk.selectPage")} checked={allOnPageSelected} onChange={togglePage} />
						</TableHead>
						<TableHead className="w-[80px]">
							{t("products.col.photo")}
						</TableHead>
						<SortableHead label={t("products.col.name")} sortKey="name" sort={view.sort} onToggle={view.toggleSort} />
						<SortableHead label={t("products.col.shop")} sortKey="shop" sort={view.sort} onToggle={view.toggleSort} />
						<SortableHead label={t("products.col.category")} sortKey="category" sort={view.sort} onToggle={view.toggleSort} />
						<SortableHead label={t("products.col.price")} sortKey="price" sort={view.sort} onToggle={view.toggleSort} align="right" className="text-right" />
						<SortableHead label={t("products.col.stock")} sortKey="stock" sort={view.sort} onToggle={view.toggleSort} align="right" className="text-right" />
						<SortableHead label={t("products.col.status")} sortKey="status" sort={view.sort} onToggle={view.toggleSort} />
						<SortableHead label={t("products.col.updated")} sortKey="updated" sort={view.sort} onToggle={view.toggleSort} />
						<TableHead className="w-12 text-right">
							<span className="sr-only">{t("products.col.actions")}</span>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody data-testid="products-rows" aria-busy={productsQuery.isFetching}>
					{load === "loading" && <TableRow>
							<TableCell colSpan={10} className="text-center text-muted-foreground">
								{t("products.loading")}
							</TableCell>
						</TableRow>}
					{load === "ready" && total === 0 && <TableRow>
							<TableCell colSpan={10} className="text-center text-muted-foreground">
								{table.hasFilters ? t("products.emptyFiltered") : t("products.empty")}
							</TableCell>
						</TableRow>}
					{items.map(p_6 => <TableRow key={p_6.id} className="cursor-pointer" data-product-id={p_6.id} onClick={() => setSelectedProductId(p_6.id)} data-state={selected.has(p_6.id) ? "selected" : undefined}>
							<TableCell onClick={e_9 => e_9.stopPropagation()}>
								<input type="checkbox" className="size-4 align-middle" aria-label={p_6.name} checked={selected.has(p_6.id)} onChange={() => toggleOne(p_6)} />
							</TableCell>
							<TableCell>
								{p_6.photos[0] ? <img src={p_6.photos[0]} alt="" className="size-12 rounded object-cover" /> : <div className="size-12 rounded bg-muted" />}
							</TableCell>
							<TableCell className="font-medium">{p_6.name}</TableCell>
							<TableCell onClick={e_10 => e_10.stopPropagation()}>
								{slugByShop.has(p_6.shopId) || p_6.shopOwnerId ? <button type="button" className="text-left font-medium text-primary hover:underline" onClick={() => openShop(p_6)}>
										{p_6.shopName ?? p_6.shopId}
									</button> : <span className="text-muted-foreground">{p_6.shopName ?? p_6.shopId}</span>}
							</TableCell>
							<TableCell className="max-w-[180px] truncate text-muted-foreground">
								{p_6.category || t("products.noCategory")}
							</TableCell>
							<TableCell className="text-right tabular-nums">
								{formatPrice(p_6.price)}
							</TableCell>
							<TableCell className="text-right tabular-nums">
								{p_6.stock}
							</TableCell>
							<TableCell>
								<Badge variant={STATUS_VARIANT[p_6.status]}>
									{t(`products.status.${p_6.status}`)}
								</Badge>
								<ComplianceRowNote product={p_6} />
							</TableCell>
							<TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
								{new Date(p_6.updatedAt).toLocaleDateString("ru-RU")}
							</TableCell>
							<TableCell className="text-right" onClick={e_11 => e_11.stopPropagation()}>
								<ProductActionsMenu product={p_6} acting={acting === p_6.id} open={openActions === p_6.id} onOpenChange={open => setOpenActions(open ? p_6.id : null)} onAction={kind_0 => {
              setOpenActions(null);
              setPending({
                kind: kind_0,
                products: [{
                  id: p_6.id,
                  name: p_6.name
                }]
              });
            }} t={t} />
							</TableCell>
						</TableRow>)}
				</TableBody>
			</Table>

			{load === "ready" && total > 0 && <DataTablePagination table={view} />}

			<AdminDetailModal open={!!selectedProductId} title={t("productDetail.summary")} fullPageHref={selectedProductId ? `/products/${selectedProductId}` : undefined} fullPageLabel={t("detailModal.openFullPage")} onOpenChange={open_0 => !open_0 && setSelectedProductId(null)}>
				{selectedProductId && <ProductDetailContent id={selectedProductId} />}
			</AdminDetailModal>

			<AdminDetailModal open={!!selectedShop} title={selectedShop?.title ?? t("storefronts.heading")} fullPageHref={selectedShop?.kind === "storefront" ? `/shops/${selectedShop.slug}` : selectedShop ? `/shops/seller/${selectedShop.userId}` : undefined} fullPageLabel={t("detailModal.openFullPage")} onOpenChange={open_1 => !open_1 && setSelectedShop(null)}>
				{selectedShop?.kind === "storefront" && <ShopDetailContent slug={selectedShop.slug} />}
				{selectedShop?.kind === "seller" && <SellerDetailContent userId={selectedShop.userId} />}
			</AdminDetailModal>

			<ConfirmDialog open={!!pending} busy={!!acting} title={pending ? pending.products.length === 1 ? `${t(dialogKey("Title"))} — ${pending.products[0]?.name ?? ""}` : t(`products_dialog.bulk.${pending.kind}Title`, {
      count: pending.products.length
    }) : ""} description={pending ? t(dialogKey("Body")) : undefined} confirmLabel={pending ? t(dialogKey("Label")) : undefined} confirmVariant={pending?.kind === "approve" ? "default" : "destructive"} onConfirm={confirmAction} onClose={() => {
      setPending(null);
      setActionError(null);
      setRejectionCode("");
      setRejectionReason("");
    }}>
				{error && pending && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
						{error}
					</div>}
				{pending?.kind === "block" && <div className="space-y-1.5">
						<label className="text-sm font-medium" htmlFor="products-block-reason">
							{t("products.block.reasonLabel")}
						</label>
						<textarea id="products-block-reason" value={rejectionReason} onChange={e_12 => setRejectionReason(e_12.target.value)} placeholder={t("products.block.reasonPlaceholder")} className="min-h-24 w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
					</div>}
				{pending?.kind === "reject" && <div className="space-y-3">
						<div className="space-y-1.5">
							<label className="text-sm font-medium" htmlFor="products-reject-code">
								{t("productDetail.rejectReasonLabel")}
							</label>
							<Select id="products-reject-code" value={rejectionCode} onChange={e_13 => setRejectionCode(e_13.target.value)}>
								<option value="">{t("productDetail.rejectReasonSelect")}</option>
								{(rejectionReasons ?? []).map(r => <option key={r.code} value={r.code}>
										{r.label}
									</option>)}
							</Select>
						</div>
						<div className="space-y-1.5">
							<label className="text-sm font-medium" htmlFor="products-reject-comment">
								{t("productDetail.rejectCommentLabel")}
							</label>
							<textarea id="products-reject-comment" value={rejectionReason} onChange={e_14 => setRejectionReason(e_14.target.value)} placeholder={t("productDetail.rejectReasonPlaceholder")} className="min-h-24 w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
						</div>
					</div>}
			</ConfirmDialog>
		</div>;
}
function ProductActionsMenu(t0) {
  const $ = _c(25);
  const {
    product,
    acting,
    open,
    onOpenChange,
    onAction,
    t
  } = t0;
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      top: 0,
      left: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [position, setPosition] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== onOpenChange || $[2] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      const updatePosition = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) {
          return;
        }
        const opensDownTop = rect.bottom + 4;
        const top = opensDownTop + 126 > window.innerHeight - 8 ? Math.max(8, rect.top - 126 - 4) : opensDownTop;
        setPosition({
          top,
          left: Math.max(8, Math.min(window.innerWidth - 144 - 8, rect.right - 144))
        });
      };
      const closeOnOutsideClick = event => {
        const target = event.target as Node;
        if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) {
          return;
        }
        onOpenChange(false);
      };
      const closeOnEscape = event_0 => {
        if (event_0.key === "Escape") {
          onOpenChange(false);
        }
      };
      updatePosition();
      document.addEventListener("pointerdown", closeOnOutsideClick);
      document.addEventListener("keydown", closeOnEscape);
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        document.removeEventListener("pointerdown", closeOnOutsideClick);
        document.removeEventListener("keydown", closeOnEscape);
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    };
    t3 = [open, onOpenChange];
    $[1] = onOpenChange;
    $[2] = open;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== onOpenChange || $[6] !== open) {
    t4 = () => onOpenChange(!open);
    $[5] = onOpenChange;
    $[6] = open;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("products.col.actions");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <MoreVertical className="size-4" />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== open || $[12] !== t4 || $[13] !== t5) {
    t7 = <Button ref={buttonRef} type="button" variant="outline" size="icon" className="size-8" onClick={t4} aria-haspopup="menu" aria-expanded={open} aria-label={t5}>{t6}</Button>;
    $[11] = open;
    $[12] = t4;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== acting || $[16] !== onAction || $[17] !== open || $[18] !== position || $[19] !== product || $[20] !== t) {
    t8 = open && createPortal(<div ref={menuRef} role="menu" className="fixed z-[9999] min-w-36 rounded-md border bg-popover p-1 text-left text-popover-foreground shadow-md" style={{
      top: position.top,
      left: position.left
    }}><button type="button" role="menuitem" className="w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-accent disabled:pointer-events-none disabled:opacity-50" onClick={() => onAction("approve")} disabled={acting || product.status === "published" || Boolean(product.compliance?.blocksPublication)} title={product.compliance?.blocksPublication ? t("compliance.detail.missing") : undefined}>{t(product.status === "blocked" ? "products.actions.unblock" : "products.actions.approve")}</button><button type="button" role="menuitem" className="w-full rounded-sm px-3 py-2 text-left text-sm text-destructive hover:bg-accent disabled:pointer-events-none disabled:opacity-50" onClick={() => onAction("reject")} disabled={acting || product.status === "rejected" || product.status === "archived" || product.status === "blocked"}>{t("products.actions.reject")}</button><button type="button" role="menuitem" className="w-full rounded-sm px-3 py-2 text-left text-sm text-destructive hover:bg-accent disabled:pointer-events-none disabled:opacity-50" onClick={() => onAction("block")} disabled={acting || product.status === "blocked" || product.status === "archived"}>{t("products.actions.block")}</button></div>, document.body);
    $[15] = acting;
    $[16] = onAction;
    $[17] = open;
    $[18] = position;
    $[19] = product;
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}